/**
 * ReviewerAccessModal — credential-gated entry to the Phase 4 board package.
 *
 * Flow:
 *  Step 1: pick role (Board Pack / Reviewer / Buyer / Admin)
 *  Step 2:
 *    - Roles 1-3 → access code input
 *    - Admin role → email + password input
 *  On submit:
 *    - success + matching role → toast + navigate("/phase4")
 *    - success + different role → toast explaining the mismatch + navigate
 *    - failure → toast + return to step 1
 *
 * Used by both the landing-page hero CTA and the header "Reviewer access"
 * link. Closing the modal returns the user to the landing page (no nav).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Eye, Briefcase, ShieldCheck, ArrowLeft, X,
  CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';

const ROLES = [
  {
    key: 'board_pack',
    label: 'Board Pack',
    description: 'Board members and transaction reviewers — short read-path.',
    icon: Shield,
    color: '#2ce1e9',
    placeholderCode: 'BOARD-PHASE4-XXXX-XXXX',
  },
  {
    key: 'reviewer',
    label: 'Reviewer',
    description: 'Avaganz / full reviewer access — every artifact incl. raw evidence.',
    icon: Eye,
    color: '#3aa6ff',
    placeholderCode: 'REVIEW-PHASE4-XXXX-XXXX',
  },
  {
    key: 'buyer',
    label: 'Buyer',
    description: 'Buyer-demo access — board pack + live workspace, no raw evidence.',
    icon: Briefcase,
    color: '#ffae5d',
    placeholderCode: 'BUYER-PHASE4-XXXX-XXXX',
  },
  {
    key: 'admin',
    label: 'Admin',
    description: 'Internal admin — sign in with your MSML Health email & password.',
    icon: ShieldCheck,
    color: '#06b6a4',
  },
];

const TOAST_DURATION = 5000;

function Toast({ tone, message, onClose }) {
  if (!message) return null;
  const toneClasses = {
    success: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200',
    info: 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100',
    error: 'border-rose-400/40 bg-rose-400/10 text-rose-200',
  };
  const Icon = tone === 'success' ? CheckCircle2 : tone === 'error' ? AlertCircle : AlertCircle;
  return (
    <div
      className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${toneClasses[tone] || toneClasses.info}`}
      role="status"
      data-testid={`access-toast-${tone}`}
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <span className="flex-1 leading-relaxed">{message}</span>
      <button onClick={onClose} className="shrink-0 opacity-60 hover:opacity-100" aria-label="dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function ReviewerAccessModal({ open, onClose }) {
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  if (!open) return null;

  const reset = () => {
    setRole(null);
    setCode('');
    setEmail('');
    setPassword('');
    setBusy(false);
    setToast(null);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const pushToast = (tone, message) => {
    setToast({ tone, message });
    if (tone !== 'error') {
      setTimeout(() => setToast(null), TOAST_DURATION);
    }
  };

  const finishSuccess = (data) => {
    pushToast(
      data.requested_role === data.granted_role ? 'success' : 'info',
      data.message,
    );
    // Small delay so the toast is visible before the route change
    setTimeout(() => {
      handleClose();
      navigate(data.redirect || '/phase4');
    }, 700);
  };

  const submitCode = async () => {
    if (!code.trim()) return pushToast('error', 'Please enter your access code.');
    setBusy(true);
    try {
      const r = await fetch('/api/phase4/access/code', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: role.key, code: code.trim() }),
      });
      const data = await r.json();
      if (data.success) {
        finishSuccess(data);
      } else {
        pushToast('error', data.message || 'Access denied.');
        // Per spec: invalid code → notification + return to landing page
        setTimeout(() => {
          handleClose();
          navigate('/');
        }, 1800);
      }
    } catch (e) {
      pushToast('error', 'Could not reach the access service. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitAdmin = async () => {
    if (!email.trim() || !password) return pushToast('error', 'Please enter your email and password.');
    setBusy(true);
    try {
      const r = await fetch('/api/phase4/access/admin-login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: 'admin', email: email.trim(), password }),
      });
      const data = await r.json();
      if (data.success) {
        finishSuccess(data);
      } else {
        pushToast('error', data.message || 'Invalid credentials.');
        setTimeout(() => {
          handleClose();
          navigate('/');
        }, 1800);
      }
    } catch (e) {
      pushToast('error', 'Could not reach the access service. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-md bg-black/50"
      onClick={handleClose}
      data-testid="reviewer-access-modal"
    >
      <div
        className="liquid-card-hero !p-6 sm:!p-8 max-w-lg w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)] transition-colors"
          aria-label="Close"
          data-testid="access-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[var(--accent-cyan)] whitespace-nowrap overflow-hidden text-ellipsis">
            MSML · Phase 4 · Reviewer Access
          </div>
          <h2
            className="mt-1 text-2xl font-semibold"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="access-modal-title"
          >
            {role ? `Access as ${role.label}` : 'Access Report Package'}
          </h2>
          <p className="text-xs text-[var(--ink-secondary)] mt-1 leading-relaxed">
            {role
              ? role.description
              : 'Please choose your reviewer role to continue. Your access code or admin credentials will be verified.'}
          </p>
        </div>

        {/* Step 1: pick role */}
        {!role && (
          <div className="space-y-2" data-testid="role-picker">
            {ROLES.map((r) => {
              const Icon = r.icon;
              return (
                <button
                  key={r.key}
                  onClick={() => setRole(r)}
                  className="w-full text-left liquid-card liquid-lift !p-3 flex items-center gap-3 group"
                  data-testid={`role-option-${r.key}`}
                >
                  <span
                    className="shrink-0 w-9 h-9 rounded-lg grid place-items-center"
                    style={{ background: `${r.color}1F`, color: r.color }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-[var(--ink-primary)]">{r.label}</span>
                    <span className="block text-[11px] text-[var(--ink-tertiary)] leading-snug">{r.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Step 2: code or email/password */}
        {role && role.key !== 'admin' && (
          <div className="space-y-3" data-testid="code-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--ink-tertiary)]">
                Access code
              </span>
              <input
                type="text"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && submitCode()}
                placeholder={role.placeholderCode}
                className="mt-1 w-full liquid-input font-mono tracking-wider text-sm"
                data-testid="access-code-input"
              />
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setRole(null)}
                className="liquid-btn text-xs"
                data-testid="access-back"
              >
                <ArrowLeft className="w-3 h-3 mr-1.5 inline" />
                Back
              </button>
              <button
                onClick={submitCode}
                disabled={busy}
                className="liquid-btn liquid-btn--primary flex-1 text-xs disabled:opacity-60"
                data-testid="access-submit-code"
              >
                {busy && <Loader2 className="w-3 h-3 mr-1.5 inline animate-spin" />}
                {busy ? 'Verifying…' : 'Verify & continue'}
              </button>
            </div>
          </div>
        )}

        {role && role.key === 'admin' && (
          <div className="space-y-3" data-testid="admin-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--ink-tertiary)]">Email</span>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@msmlhealth.com"
                className="mt-1 w-full liquid-input text-sm"
                data-testid="access-admin-email"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--ink-tertiary)]">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitAdmin()}
                placeholder="••••••••"
                className="mt-1 w-full liquid-input text-sm"
                data-testid="access-admin-password"
              />
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setRole(null)}
                className="liquid-btn text-xs"
                data-testid="access-back-admin"
              >
                <ArrowLeft className="w-3 h-3 mr-1.5 inline" />
                Back
              </button>
              <button
                onClick={submitAdmin}
                disabled={busy}
                className="liquid-btn liquid-btn--primary flex-1 text-xs disabled:opacity-60"
                data-testid="access-submit-admin"
              >
                {busy && <Loader2 className="w-3 h-3 mr-1.5 inline animate-spin" />}
                {busy ? 'Signing in…' : 'Sign in & continue'}
              </button>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className="mt-4">
            <Toast tone={toast.tone} message={toast.message} onClose={() => setToast(null)} />
          </div>
        )}

        <p className="text-[10px] text-[var(--ink-tertiary)] mt-4 leading-relaxed">
          By continuing you agree access codes and admin credentials may be logged
          for audit. Codes expire and may be rotated; contact MSML Health for a new one.
        </p>
      </div>
    </div>
  );
}
